import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS } from '@makila/shared';
import { PAYMENT_METHODS } from '@makila/fiscal';
import { AtSign, Hash, ImagePlus, KeyRound, Landmark, Pencil, Rocket, ShieldCheck, Smartphone, Trash2, UserPlus, UserRound } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Alert, Badge, Button, Card, Checkbox, Chips, Empty, Field, Input, Loading, Modal, PageHeader, Select, SuggestInput, Table, Tabs, Td, Textarea, Th, errorText, fieldErrors, useConfirm, useToast,
} from '../components/ui';
import { api, del, get, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime, docLabel } from '../lib/format';
import { BANKS, MOBILE_WALLETS, PROVINCES } from '../lib/lists';
import { AutomationSettings } from './automation';

type Tab = 'empresa' | 'documentos' | 'bancos' | 'automacao' | 'equipa' | 'seguranca';

function useCompanyForm(c: any) {
  return useState({
    legalName: c.legal_name, tradeName: c.trade_name ?? '', nuit: c.nuit, vatRegime: c.vat_regime, address: c.address, city: c.city,
    province: c.province ?? '', postalCode: c.postal_code ?? '', phone: c.phone ?? '', email: c.email ?? '', website: c.website ?? '',
    capitalSocial: c.capital_social ?? '', registryInfo: c.registry_info ?? '', brandColor: c.brand_color ?? '#a6070c',
    documentFooter: c.document_footer ?? '', invoiceTerms: c.invoice_terms ?? '', defaultPaymentTermsDays: String(c.default_payment_terms_days),
    quoteValidityDays: String(c.quote_validity_days), maxDiscountWithoutApproval: c.max_discount_without_approval != null ? String(Number(c.max_discount_without_approval)) : '',
    documentTemplate: c.document_template ?? 'classico', defaultPaymentMethod: c.default_payment_method ?? '',
  });
}

function CompanyAndDocuments({ data, which, onSaved }: { data: any; which: 'empresa' | 'documentos'; onSaved: () => void }) {
  const toast = useToast();
  const { can } = useAuth();
  const [f, setF] = useCompanyForm(data.company);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const editable = can('settings:edit');
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErrors({});
    try { await put('/settings/company', { ...f, maxDiscountWithoutApproval: f.maxDiscountWithoutApproval || null, defaultPaymentMethod: f.defaultPaymentMethod || null }); toast('Guardado. Documentos já emitidos mantêm os dados da altura.'); onSaved(); }
    catch (ex) { setErrors(fieldErrors(ex)); toast(errorText(ex), 'error'); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} noValidate>
      <fieldset disabled={!editable} className="space-y-6">
        {which === 'empresa' ? <>
          <Card title="Identificação fiscal" subtitle="Impressa no cabeçalho de todos os documentos">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Firma / denominação social" required className="sm:col-span-2" error={errors.legalName}><Input value={f.legalName} onChange={set('legalName')} /></Field>
              <Field label="NUIT" required error={errors.nuit}><Input value={f.nuit} onChange={set('nuit')} /></Field>
              <Field label="Nome comercial" className="sm:col-span-2" hint="Usado na aplicação e nos emails — os documentos levam só a firma"><Input value={f.tradeName} onChange={set('tradeName')} /></Field>
              <Field label="Regime de IVA" hint="Determina as menções obrigatórias">
                <Select value={f.vatRegime} onChange={set('vatRegime')}><option value="normal">Normal</option><option value="simplified">Simplificado (art. 42–45)</option><option value="exempt">Isenção</option><option value="ispc">ISPC</option></Select>
              </Field>
              <Field label="Registo comercial" className="sm:col-span-3"><Input value={f.registryInfo} onChange={set('registryInfo')} placeholder="Matrícula n.º … na Conservatória do Registo das Entidades Legais de …" /></Field>
            </div>
          </Card>
          <Card title="Sede e contactos">
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label="Morada" required className="sm:col-span-2" error={errors.address}><Input value={f.address} onChange={set('address')} /></Field>
              <Field label="Cidade" required error={errors.city}><Input value={f.city} onChange={set('city')} /></Field>
              <Field label="Província"><SuggestInput suggestions={PROVINCES} value={f.province} onChange={set('province')} /></Field>
              <Field label="Telefone"><Input value={f.phone} onChange={set('phone')} /></Field>
              <Field label="Email" error={errors.email}><Input type="email" value={f.email} onChange={set('email')} /></Field>
              <Field label="Website"><Input value={f.website} onChange={set('website')} /></Field>
              <Field label="Código postal"><Input value={f.postalCode} onChange={set('postalCode')} /></Field>
            </div>
          </Card>
        </> : <>
          <Card title="Modelo de impressão" subtitle="Aplica-se aos PDF de todos os documentos emitidos a partir de agora e às reimpressões">
            <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Modelo de impressão">
              {([['classico', 'Clássico', 'Sóbrio, a preto e cinzento, ao estilo Primavera/PHC: blocos com linhas finas e totais no fundo da página.'],
                ['moderno', 'Moderno', 'Faixa com a cor da empresa no cabeçalho e na tabela; títulos em destaque.']] as const).map(([v, t, d]) => (
                <button key={v} type="button" role="radio" aria-checked={f.documentTemplate === v} onClick={() => setF({ ...f, documentTemplate: v })}
                  className={`rounded-xl border-2 p-4 text-left transition ${f.documentTemplate === v ? 'border-brand-600 bg-brand-500/5' : 'border-[var(--border)] hover:border-[var(--border-strong)]'}`}>
                  <div className="mb-3 h-28 rounded-lg border border-[var(--border)] bg-white p-2.5">
                    <div className={`h-3 w-20 rounded-sm ${v === 'moderno' ? '' : 'bg-gray-300'}`} style={v === 'moderno' ? { background: f.brandColor } : undefined} />
                    <div className="mt-2 flex justify-between"><div className="h-1.5 w-16 rounded bg-gray-200" /><div className="h-1.5 w-12 rounded bg-gray-200" /></div>
                    <div className="mt-2 h-2 rounded-sm" style={{ background: v === 'moderno' ? f.brandColor : '#e5e7eb' }} />
                    {[0, 1, 2].map((i) => <div key={i} className="mt-1.5 h-1 rounded bg-gray-100" />)}
                    <div className="mt-3 ml-auto h-3 w-16 rounded-sm" style={{ background: v === 'moderno' ? f.brandColor : '#374151' }} />
                  </div>
                  <div className="font-semibold">{t}</div><div className="mt-0.5 text-xs text-[var(--muted)]">{d}</div>
                </button>
              ))}
            </div>
          </Card>
          <Card title="Aspecto dos documentos">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Cor principal"><div className="flex items-center gap-2"><input type="color" aria-label="Cor principal" className="h-9.5 w-14 cursor-pointer rounded-lg border border-[var(--border-strong)]" value={f.brandColor} onChange={set('brandColor')} /><Input value={f.brandColor} onChange={set('brandColor')} /></div></Field>
              <Field label="Rodapé" className="sm:col-span-2"><Input value={f.documentFooter} onChange={set('documentFooter')} placeholder="Obrigado pela preferência" /></Field>
              <Field label="Condições gerais impressas" className="sm:col-span-3" hint="Ex.: condições de pagamento, garantia, devoluções"><Textarea rows={3} value={f.invoiceTerms} onChange={set('invoiceTerms')} /></Field>
            </div>
          </Card>
          <Card title="Regras comerciais">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Prazo de pagamento padrão (dias)" error={errors.defaultPaymentTermsDays}><Input inputMode="numeric" value={f.defaultPaymentTermsDays} onChange={set('defaultPaymentTermsDays')} /></Field>
              <Field label="Validade das pró-formas (dias)" error={errors.quoteValidityDays}><Input inputMode="numeric" value={f.quoteValidityDays} onChange={set('quoteValidityDays')} /></Field>
              <Field label="Desconto máximo sem aprovação (%)" hint="Vazio = sem tecto" error={errors.maxDiscountWithoutApproval}><Input inputMode="decimal" value={f.maxDiscountWithoutApproval} onChange={set('maxDiscountWithoutApproval')} /></Field>
              <Field label="Forma de pagamento por omissão" hint="Pré-seleccionada em facturas e pró-formas">
                <Select value={f.defaultPaymentMethod} onChange={set('defaultPaymentMethod')}>
                  <option value="">—</option>{Object.entries(PAYMENT_METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </Select>
              </Field>
            </div>
          </Card>
        </>}
        {editable && <div className="flex justify-end"><Button type="submit" loading={busy}>Guardar alterações</Button></div>}
      </fieldset>
    </form>
  );
}

function LogoCard() {
  const toast = useToast();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['logo'], queryFn: () => get('/settings/logo') });
  async function upload(file: File) {
    const fd = new FormData(); fd.append('file', file);
    try { await api('POST', '/settings/logo', fd); toast('Logótipo actualizado'); q.refetch(); } catch (e) { toast(errorText(e), 'error'); }
  }
  return (
    <Card title="Logótipo">
      <div className="flex h-28 items-center justify-center rounded-xl border border-dashed border-[var(--border-strong)] bg-white">{q.data?.logo ? <img src={q.data.logo} alt="Logótipo" className="max-h-20 max-w-[85%] object-contain" /> : <span className="text-xs text-gray-400">Sem logótipo</span>}</div>
      {can('settings:edit') && <label className="mt-3 block cursor-pointer"><span className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-[var(--surface)] text-sm font-medium ring-1 ring-[var(--border-strong)] hover:bg-[var(--surface-2)]"><ImagePlus className="size-4" />Carregar imagem</span>
        <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} /></label>}
      <p className="mt-2 text-xs text-[var(--muted)]">PNG, JPEG ou WebP até 1 MB. Documentos já emitidos mantêm o logótipo da altura.</p>
    </Card>
  );
}

function FiscalCard({ allowed, onChange }: { allowed: boolean; onChange: () => void }) {
  const { session, can } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const goLive = async () => {
    if (!(await confirm({ title: 'Activar a emissão fiscal?', description: 'A partir daqui os documentos têm valor fiscal, usam as séries definitivas e entram na comunicação mensal à AT. Não é possível voltar ao modo formação.', confirmLabel: 'Activar emissão fiscal' }))) return;
    try { await post('/settings/go-live'); toast('Emissão fiscal activada'); onChange(); } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <Card title="Emissão fiscal">
      <div id="emissao" />
      {session?.tenant.training_mode ? (
        <div className="space-y-3 text-sm">
          <Alert tone="warn" title="Modo formação">Pode experimentar tudo; os documentos não têm valor fiscal.</Alert>
          {!allowed ? <p className="text-[var(--muted)]">O programa aguarda a autorização da Autoridade Tributária. A emissão fiscal fica disponível logo que seja concedida.</p>
            : !session.user.email_verified_at ? <p className="text-[var(--muted)]">Para activar, confirme primeiro o seu email (veja o aviso no topo da página).</p>
              : !session.tenant.nuit_verified_at ? <Alert tone="info" title="NUIT por confirmar">Antes de emitir com valor fiscal, o Makila confirma que a empresa é a titular do NUIT {session.tenant.nuit}. Envie a certidão do NUIT ou o registo comercial ao suporte.</Alert>
                : can('settings:edit') && <Button className="w-full" icon={<Rocket className="size-4" />} onClick={goLive}>Activar emissão fiscal</Button>}
        </div>
      ) : <Alert tone="success" title="Emissão fiscal activa">Documentos com valor fiscal, assinados e comunicáveis à AT.</Alert>}
    </Card>
  );
}

type BankForm = { kind: 'bank' | 'mobile'; bankName: string; accountHolder: string; accountNumber: string; nib: string; iban: string; swift: string; entity: string; reference: string; showOnDocuments: boolean };
const EMPTY_BANK: BankForm = { kind: 'bank', bankName: '', accountHolder: '', accountNumber: '', nib: '', iban: '', swift: '', entity: '', reference: '', showOnDocuments: true };

/** Contas onde os clientes pagam: bancos e carteiras móveis, com entidade e referência para pagamento de serviços. */
function Banks({ banks, onChange }: { banks: any[]; onChange: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useAuth();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [f, setF] = useState<BankForm>(EMPTY_BANK);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof BankForm) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const mobile = f.kind === 'mobile';
  const open = (b?: any) => {
    setErrors({});
    setF(b ? {
      kind: b.kind ?? 'bank', bankName: b.bank_name, accountHolder: b.account_holder ?? '', accountNumber: b.account_number ?? '', nib: b.nib ?? '', iban: b.iban ?? '',
      swift: b.swift ?? '', entity: b.entity ?? '', reference: b.reference ?? '', showOnDocuments: b.show_on_documents,
    } : EMPTY_BANK);
    setEditing(b ? b.id : 'new');
  };
  const save = async () => {
    setErrors({});
    const body = { ...f, ...Object.fromEntries((['accountHolder', 'accountNumber', 'nib', 'iban', 'swift', 'entity', 'reference'] as const).map((k) => [k, f[k].trim() || null])) };
    try {
      if (editing === 'new') { await post('/settings/banks', body); toast('Conta adicionada'); }
      else { await put(`/settings/banks/${editing}`, body); toast('Conta guardada'); }
      setEditing(null); onChange();
    } catch (e) { setErrors(fieldErrors(e)); toast(errorText(e), 'error'); }
  };
  const detail = (b: any) => [b.nib && `NIB ${b.nib}`, b.iban && `IBAN ${b.iban}`, b.swift && `SWIFT ${b.swift}`, b.entity && `Entidade ${b.entity}`, b.reference && `Ref. ${b.reference}`].filter(Boolean).join(' · ') || '—';
  return (
    <Card title="Contas bancárias e carteiras móveis" subtitle="Impressas nos documentos para o cliente saber onde pagar — documentos já emitidos mantêm os dados que tinham" padded={false}
      actions={can('settings:edit') && <Button size="sm" icon={<Landmark className="size-4" />} onClick={() => open()}>Adicionar conta</Button>}>
      {!banks.length ? <Empty icon={<Landmark />} title="Sem contas">Adicione as contas bancárias e as carteiras móveis (M-Pesa, e-Mola, mKesh) onde os clientes podem pagar.</Empty> : (
        <Table>
          <thead><tr><Th>Banco / carteira</Th><Th>Titular</Th><Th>Conta / n.º</Th><Th>NIB, IBAN, entidade…</Th><Th /></tr></thead>
          <tbody>{banks.map((b) => (
            <tr key={b.id}>
              <Td className="font-medium"><span className="flex items-center gap-2">{b.kind === 'mobile' ? <Smartphone className="size-4 text-[var(--muted)]" /> : <Landmark className="size-4 text-[var(--muted)]" />}{b.bank_name}{!b.show_on_documents && <Badge>fora dos documentos</Badge>}</span></Td>
              <Td>{b.account_holder ?? '—'}</Td><Td className="tabular-nums">{b.account_number ?? '—'}</Td><Td className="text-xs tabular-nums">{detail(b)}</Td>
              <Td className="text-right whitespace-nowrap">{can('settings:edit') && <>
                <Button size="sm" variant="ghost" iconOnly aria-label="Editar" icon={<Pencil className="size-4" />} onClick={() => open(b)} />
                <Button size="sm" variant="ghost" iconOnly aria-label="Remover" icon={<Trash2 className="size-4" />} onClick={async () => { if (await confirm({ title: `Remover a conta ${b.bank_name}?`, tone: 'danger', confirmLabel: 'Remover' })) { await del(`/settings/banks/${b.id}`); onChange(); } }} />
              </>}</Td></tr>
          ))}</tbody>
        </Table>
      )}
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? 'Nova conta' : 'Editar conta'} icon={mobile ? <Smartphone /> : <Landmark />}
        footer={<><Button variant="secondary" onClick={() => setEditing(null)}>Cancelar</Button><Button onClick={save} disabled={f.bankName.trim().length < 2}>{editing === 'new' ? 'Adicionar' : 'Guardar'}</Button></>}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tipo" className="sm:col-span-2"><Chips options={[{ id: 'bank', label: 'Conta bancária' }, { id: 'mobile', label: 'Carteira móvel' }]} value={f.kind} onChange={(kind) => setF({ ...f, kind, bankName: '' })} /></Field>
          <Field label={mobile ? 'Carteira' : 'Banco'} required hint="Escolha da lista ou escreva o nome — é o que sai impresso nos documentos">
            <SuggestInput key={f.kind} suggestions={mobile ? MOBILE_WALLETS : BANKS} autoFocus value={f.bankName} onChange={set('bankName')} /></Field>
          <Field label="Titular"><Input value={f.accountHolder} onChange={set('accountHolder')} /></Field>
          <Field label={mobile ? 'Número da carteira' : 'N.º de conta'} error={errors.accountNumber}>
            <Input value={f.accountNumber} inputMode={mobile ? 'tel' : undefined} placeholder={mobile ? '84 000 0000' : undefined} onChange={set('accountNumber')} /></Field>
          {!mobile && <>
            <Field label="NIB" error={errors.nib} hint="21 dígitos"><Input inputMode="numeric" value={f.nib} onChange={set('nib')} /></Field>
            <Field label="IBAN"><Input value={f.iban} onChange={set('iban')} /></Field>
            <Field label="SWIFT"><Input value={f.swift} onChange={set('swift')} /></Field>
          </>}
          <Field label="Entidade" error={errors.entity} hint="Se o banco ou operador lhe atribuiu uma, para pagamento de serviços"><Input inputMode="numeric" value={f.entity} onChange={set('entity')} /></Field>
          <Field label="Referência" error={errors.reference} hint="Opcional — a que o cliente indica ao pagar"><Input value={f.reference} onChange={set('reference')} /></Field>
          <div className="sm:col-span-2"><Checkbox label="Mostrar nos documentos" checked={f.showOnDocuments} onChange={(v) => setF({ ...f, showOnDocuments: v })} /></div>
        </div>
      </Modal>
    </Card>
  );
}

function Team() {
  const toast = useToast();
  const confirm = useConfirm();
  const { can, session } = useAuth();
  const q = useQuery({ queryKey: ['users'], queryFn: () => get('/settings/users') });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', role: 'seller' });
  /** Resultado do convite: null enquanto o formulário está aberto. */
  const [sent, setSent] = useState<{ email: string; newAccount: boolean } | null>(null);
  const invite = async () => {
    try { const r = await post('/settings/users', f); setSent({ email: f.email, newAccount: r.newAccount }); q.refetch(); } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <Card title="Equipa" subtitle="Quem tem acesso a esta empresa e com que papel" padded={false}
      actions={can('users:create') && <Button size="sm" icon={<UserPlus className="size-4" />} onClick={() => { setF({ name: '', email: '', role: 'seller' }); setSent(null); setOpen(true); }}>Convidar</Button>}>
      {q.isLoading ? <div className="p-5"><Loading rows={3} /></div> : (
        <Table>
          <thead><tr><Th>Utilizador</Th><Th>Papel</Th><Th>2FA</Th><Th>Última entrada</Th><Th /></tr></thead>
          <tbody>{q.data?.items.filter((u: any) => u.active).map((u: any) => (
            <tr key={u.id}>
              <Td><div className="font-medium">{u.name} {u.id === session?.user.id && <Badge tone="teal">você</Badge>}</div><div className="text-xs text-[var(--muted)]">{u.email}</div></Td>
              <Td>{ROLE_LABELS[u.role as keyof typeof ROLE_LABELS]}</Td>
              <Td>{u.totp_enabled ? <Badge tone="green">Activa</Badge> : <Badge>Não</Badge>}</Td><Td className="text-[var(--muted)]">{dateTime(u.last_login_at)}</Td>
              <Td className="text-right">{u.role !== 'owner' && can('users:delete') && u.id !== session?.user.id && <Button size="sm" variant="ghost" onClick={async () => { if (await confirm({ title: `Remover ${u.name}?`, description: 'Deixa de ter acesso a esta empresa. O histórico na auditoria mantém-se.', tone: 'danger', confirmLabel: 'Remover acesso' })) { await del(`/settings/users/${u.id}`); q.refetch(); } }}>Remover</Button>}</Td>
            </tr>
          ))}</tbody>
        </Table>
      )}
      <div className="border-t border-[var(--border)] p-5 text-xs text-[var(--muted)]">
        <b>Papéis:</b> Dono e Administrador gerem tudo · Contabilista consulta e exporta · Vendedor cria e emite documentos · Consulta só vê.
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Convidar utilizador" icon={<UserPlus />}
        footer={sent === null ? <><Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={invite} disabled={!f.email.includes('@') || f.name.length < 2}>Convidar</Button></> : <Button onClick={() => setOpen(false)}>Concluir</Button>}>
        {sent !== null ? (
          sent.newAccount ? <Alert tone="success" title="Convite enviado">Enviámos para {sent.email} um endereço para a pessoa definir a sua própria password (válido 7 dias). Ninguém mais a conhece.</Alert>
            : <Alert tone="success">A pessoa já tinha conta Makila: foi adicionada a esta empresa e avisada por email.</Alert>
        ) : (
          <div className="space-y-4">
            <Field label="Nome" required><Input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Email" required><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="Papel"><Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>{(['admin', 'accountant', 'seller', 'viewer'] as const).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select></Field>
          </div>
        )}
      </Modal>
    </Card>
  );
}

/** Nome e telemóvel de quem usa a conta — vão na assinatura dos emails aos clientes. */
function Profile() {
  const { session, reload } = useAuth();
  const toast = useToast();
  const [p, setP] = useState({ name: session?.user.name ?? '', phone: session?.user.phone ?? '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault(); setErr({}); setBusy(true);
    try { await post('/auth/profile', { name: p.name, phone: p.phone || null }); toast('Perfil guardado'); reload(); }
    catch (ex) { setErr(fieldErrors(ex)); if (!Object.keys(fieldErrors(ex)).length) toast(errorText(ex), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <Card title={<span className="flex items-center gap-2"><UserRound className="size-4" />O meu perfil</span>} subtitle="Aparece na assinatura dos emails que envia a clientes, por baixo do nome: «Departamento Comercial».">
      <form onSubmit={save} className="space-y-4">
        <Field label="Nome" required error={err.name}><Input value={p.name} autoComplete="name" onChange={(e) => setP({ ...p, name: e.target.value })} /></Field>
        <Field label="Telemóvel" hint="Opcional — vai na assinatura" error={err.phone}><Input value={p.phone} type="tel" autoComplete="tel" placeholder="+258 84 000 0000" onChange={(e) => setP({ ...p, phone: e.target.value })} /></Field>
        <div className="flex items-center justify-between gap-3">
          <span className="truncate text-xs text-[var(--muted)]">{session?.user.email}</span>
          <Button type="submit" loading={busy} disabled={p.name.trim().length < 2}>Guardar</Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Mudar o email de entrada: pede a password e só troca depois de o endereço
 * novo ser confirmado pelo link que lá chega (o antigo é avisado).
 */
function EmailChange() {
  const { session } = useAuth();
  const toast = useToast();
  const [f, setF] = useState({ email: '', password: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr({}); setBusy(true);
    try { await post('/auth/email', f); setSentTo(f.email.trim()); setF({ email: '', password: '' }); }
    catch (ex) { const fe = fieldErrors(ex); setErr(Object.keys(fe).length ? fe : { email: errorText(ex) }); if (!Object.keys(fe).length) toast(errorText(ex), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <Card title={<span className="flex items-center gap-2"><AtSign className="size-4" />Email da conta</span>} subtitle={<>Entra com <b>{session?.user.email}</b>. É também para aí que vão as cópias dos documentos que envia.</>}>
      {sentTo && <div className="mb-4"><Alert tone="success" title="Confirme o novo email">Enviámos um link para <b>{sentTo}</b> (válido 48 horas). O email da conta só muda depois de o abrir; até lá continua a entrar com o actual.</Alert></div>}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Novo email" error={err.email}><Input type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Password actual" error={err.password} hint="Para confirmar que é você"><Input type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
        <Button type="submit" loading={busy} disabled={!f.email.includes('@') || !f.password}>Mudar email</Button>
      </form>
    </Card>
  );
}

function Security() {
  const { session, reload } = useAuth();
  const toast = useToast();
  const [pw, setPw] = useState({ current: '', password: '', confirm: '' });
  const [pwErr, setPwErr] = useState('');
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const change = async (e: FormEvent) => {
    e.preventDefault(); setPwErr('');
    if (pw.password !== pw.confirm) { setPwErr('As passwords não coincidem'); return; }
    try { await post('/auth/password', { current: pw.current, password: pw.password }); toast('Password alterada'); setPw({ current: '', password: '', confirm: '' }); }
    catch (ex) { setPwErr(errorText(ex)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Profile />
      <EmailChange />
      <Card title={<span className="flex items-center gap-2"><KeyRound className="size-4" />Mudar password</span>}>
        <form onSubmit={change} className="space-y-4">
          <Field label="Password actual"><Input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></Field>
          <Field label="Nova password" hint="Mínimo 10 caracteres"><Input type="password" autoComplete="new-password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} /></Field>
          <Field label="Confirmar nova password" error={pwErr || undefined}><Input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></Field>
          <Button type="submit" disabled={!pw.current || pw.password.length < 10}>Mudar password</Button>
        </form>
        <div className="mt-5 border-t border-[var(--border)] pt-4 text-sm">
          <p className="text-[var(--muted)]">A sessão termina após 24 horas sem uso e, no máximo, 7 dias depois de entrar. Se deixou a conta aberta noutro computador ou telemóvel, pode fechá-la já.</p>
          <Button className="mt-3" variant="secondary" size="sm" onClick={async () => { try { const r = await post('/auth/sessions/revoke-others'); toast(r.revoked ? `${r.revoked} sessão(ões) terminada(s) noutros dispositivos` : 'Não havia outras sessões abertas'); } catch (e) { toast(errorText(e), 'error'); } }}>Terminar sessão nos outros dispositivos</Button>
        </div>
      </Card>
      <Card title={<span className="flex items-center gap-2"><ShieldCheck className="size-4" />Verificação em dois passos</span>}>
        {session?.user.totp_enabled && !codes ? <Alert tone="success" title="Activa">Cada entrada pede um código da aplicação autenticadora.</Alert>
          : codes ? (
            <div className="text-sm"><Alert tone="warn" title="Guarde os códigos de recuperação">Servem para entrar se perder o telemóvel. Aparecem só agora.</Alert>
              <div className="mt-3 grid grid-cols-2 gap-1.5 font-mono">{codes.map((c) => <code key={c} className="rounded bg-[var(--surface-2)] px-2 py-1">{c}</code>)}</div></div>
          ) : !setup ? (
            <div className="space-y-3 text-sm"><p className="text-[var(--muted)]">Proteja a conta com um código de 6 dígitos de uma aplicação autenticadora (Google Authenticator, Microsoft Authenticator, Authy).</p>
              <Button variant="secondary" icon={<ShieldCheck className="size-4" />} onClick={async () => { try { setSetup(await post('/auth/2fa/setup') as never); } catch (e) { toast(errorText(e), 'error'); } }}>Activar</Button></div>
          ) : (
            <div className="space-y-3 text-sm">
              <p>1. Na aplicação, adicione uma conta com esta chave:</p>
              <code className="block rounded-lg bg-[var(--surface-2)] p-3 font-mono break-all">{setup.secret}</code>
              <p>2. Introduza o código que a aplicação mostra:</p>
              <div className="flex gap-2"><Input inputMode="numeric" maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} aria-label="Código de 6 dígitos" />
                <Button onClick={async () => { try { const r = await post('/auth/2fa/enable', { code }); setCodes(r.recoveryCodes); reload(); } catch (e) { toast(errorText(e), 'error'); } }}>Confirmar</Button></div>
            </div>
          )}
      </Card>
    </div>
  );
}

export function SettingsPage() {
  const { session, reload, can } = useAuth();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  // Segurança (password, 2FA) é de todos; o resto exige ver as definições da empresa.
  const canCompany = can('settings:view');
  const asked = (sp.get('tab') as Tab) || (canCompany ? 'empresa' : 'seguranca');
  const tab: Tab = canCompany || asked === 'seguranca' || (asked === 'equipa' && can('users:view')) ? asked : 'seguranca';
  const q = useQuery({ queryKey: ['settings'], queryFn: () => get('/settings/company'), enabled: canCompany });
  if (canCompany && (q.isLoading || !q.data)) return <Loading />;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['settings'] }); reload(); };
  const setTab = (t: Tab) => { const n = new URLSearchParams(sp); n.set('tab', t); setSp(n, { replace: true }); };
  return (
    <>
      <PageHeader eyebrow="Definições" title="Definições" subtitle={`${session?.tenant.legal_name} · plano ${session?.tenant.plan_name}`} />
      <Tabs value={tab} onChange={setTab} className="mb-6" tabs={[
        { id: 'empresa', label: 'Empresa', hidden: !canCompany }, { id: 'documentos', label: 'Documentos', hidden: !canCompany }, { id: 'bancos', label: 'Contas de pagamento', hidden: !canCompany }, { id: 'automacao', label: 'Automação', hidden: !canCompany },
        { id: 'equipa', label: 'Equipa', hidden: !can('users:view') }, { id: 'seguranca', label: 'Perfil e segurança' },
      ]} />
      {canCompany && q.data && (tab === 'empresa' || tab === 'documentos') && (
        <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
          <CompanyAndDocuments key={tab} which={tab} data={q.data} onSaved={refresh} />
          <div className="space-y-6"><LogoCard /><FiscalCard allowed={q.data.fiscalIssuingAllowed} onChange={refresh} /></div>
        </div>
      )}
      {canCompany && q.data && tab === 'bancos' && <Banks banks={q.data.banks} onChange={refresh} />}
      {canCompany && tab === 'automacao' && <div className="max-w-3xl"><AutomationSettings /></div>}
      {tab === 'equipa' && <Team />}
      {tab === 'seguranca' && <Security />}
    </>
  );
}

export function SeriesPage() {
  const q = useQuery({ queryKey: ['series'], queryFn: () => get('/settings/series') });
  return (
    <>
      <PageHeader eyebrow="Fiscal" title="Séries de numeração" subtitle="Criadas automaticamente por tipo e ano. A numeração é atribuída na emissão, sem buracos, e cada série tem a sua cadeia de assinaturas." />
      <Card padded={false}>
        {q.isLoading ? <div className="p-5"><Loading /></div> : !q.data?.items.length ? <Empty icon={<Hash />} title="Ainda sem séries">A primeira série de cada tipo nasce com o primeiro documento.</Empty> : (
          <Table>
            <thead><tr><Th>Série</Th><Th>Tipo</Th><Th>Ano</Th><Th className="text-right">Emitidos</Th><Th className="text-right">Rascunhos</Th><Th className="text-right">Próximo n.º</Th><Th>Último documento</Th><Th>Assinatura</Th></tr></thead>
            <tbody>{q.data.items.map((s: any) => (
              <tr key={s.id}>
                <Td className="font-semibold tabular-nums">{s.prefix}</Td><Td>{docLabel(s.doc_type)} {s.training && <Badge tone="blue">Formação</Badge>}</Td><Td>{s.year}</Td>
                <Td className="num">{s.issued}</Td><Td className="num text-[var(--muted)]">{s.drafts}</Td><Td className="num font-medium">{s.next_number}</Td>
                <Td>{date(s.last_issue_date)}</Td>
                <Td>{s.last_hash ? <code className="text-xs text-[var(--muted)]">{[0, 10, 20, 30].map((i) => s.last_hash[i]).join('')}</code> : '—'}</Td>
              </tr>
            ))}</tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

